import { _ as deleteRequest, a as listAttachmentDescriptions, c as getRequest, d as getOperatorCounts, f as getNeedsAttention, g as exportRequests, h as getApplicantNames, i as rejectRequest, l as getOperatorRequests, m as getCounts, n as submitRequest, o as importResult, p as getHistory, r as setAttachmentDescription, s as getRequests, t as updateDraft, u as getOperatorFilterValues, v as deleteAttachmentDescription, y as createRequest } from "./updateDraft.js";
import { infiniteQueryOptions, mutationOptions, queryOptions, useInfiniteQuery, useMutation, useQuery } from "@tanstack/react-query";
//#region src/__generated__/external/application/hooks/useCreateRequest.ts
const createRequestMutationKey = () => [{ url: "/api/mdm/external/api/external/requests" }];
function createRequestMutationOptions(config = {}) {
	const mutationKey = createRequestMutationKey();
	return mutationOptions({
		mutationKey,
		mutationFn: async ({ body }) => {
			const { data } = await createRequest({
				...config,
				body,
				throwOnError: true
			});
			return data;
		}
	});
}
/**
* @description Создаёт новую заявку в статусе DRAFT
* @summary Создать черновик заявки
* {@link /api/mdm/external/api/external/requests}
*/
function useCreateRequest(options = {}) {
	const { mutation = {}, client: config = {} } = options ?? {};
	const { client: queryClient, ...mutationOptions } = mutation;
	const mutationKey = mutationOptions.mutationKey ?? createRequestMutationKey();
	const baseOptions = createRequestMutationOptions(config);
	return useMutation({
		...baseOptions,
		mutationKey,
		...mutationOptions
	}, queryClient);
}
//#endregion
//#region src/__generated__/external/application/hooks/useDeleteAttachmentDescription.ts
const deleteAttachmentDescriptionMutationKey = () => [{ url: "/api/mdm/external/api/external/requests/:id/attachments/:fileId" }];
function deleteAttachmentDescriptionMutationOptions(config = {}) {
	const mutationKey = deleteAttachmentDescriptionMutationKey();
	return mutationOptions({
		mutationKey,
		mutationFn: async ({ path }) => {
			const { data } = await deleteAttachmentDescription({
				...config,
				path,
				throwOnError: true
			});
			return data;
		}
	});
}
/**
* @description Удаляет описание файла. Только владелец, только в статусе DRAFT.
* @summary Удалить описание вложения
* {@link /api/mdm/external/api/external/requests/:id/attachments/:fileId}
*/
function useDeleteAttachmentDescription(options = {}) {
	const { mutation = {}, client: config = {} } = options ?? {};
	const { client: queryClient, ...mutationOptions } = mutation;
	const mutationKey = mutationOptions.mutationKey ?? deleteAttachmentDescriptionMutationKey();
	const baseOptions = deleteAttachmentDescriptionMutationOptions(config);
	return useMutation({
		...baseOptions,
		mutationKey,
		...mutationOptions
	}, queryClient);
}
//#endregion
//#region src/__generated__/external/application/hooks/useDeleteRequest.ts
const deleteRequestMutationKey = () => [{ url: "/api/mdm/external/api/external/requests/:id" }];
function deleteRequestMutationOptions(config = {}) {
	const mutationKey = deleteRequestMutationKey();
	return mutationOptions({
		mutationKey,
		mutationFn: async ({ path }) => {
			const { data } = await deleteRequest({
				...config,
				path,
				throwOnError: true
			});
			return data;
		}
	});
}
/**
* @description Soft-delete: заявка помечается удалённой и пропадает у владельца
* из списка и деталей. Допускается только в статусе DRAFT.
* @summary Удалить черновик заявки
* {@link /api/mdm/external/api/external/requests/:id}
*/
function useDeleteRequest(options = {}) {
	const { mutation = {}, client: config = {} } = options ?? {};
	const { client: queryClient, ...mutationOptions } = mutation;
	const mutationKey = mutationOptions.mutationKey ?? deleteRequestMutationKey();
	const baseOptions = deleteRequestMutationOptions(config);
	return useMutation({
		...baseOptions,
		mutationKey,
		...mutationOptions
	}, queryClient);
}
//#endregion
//#region src/__generated__/external/application/hooks/useExportRequests.ts
const exportRequestsMutationKey = () => [{ url: "/api/mdm/external/api/external/operator/requests/export" }];
function exportRequestsMutationOptions(config = {}) {
	const mutationKey = exportRequestsMutationKey();
	return mutationOptions({
		mutationKey,
		mutationFn: async ({ body }) => {
			const { data } = await exportRequests({
				...config,
				body,
				throwOnError: true
			});
			return data;
		}
	});
}
/**
* @description         Формирует один ZIP-архив, где каждая заявка лежит в своей папке request_<id>
*         (request.json, request.pdf, comments.json и прикреплённые файлы).
*         Переводит каждую заявку в статус IN_REVIEW. Fail-fast: любая невалидная заявка отменяет весь экспорт.
* @summary Экспорт ZIP-архива списка заявок
* {@link /api/mdm/external/api/external/operator/requests/export}
*/
function useExportRequests(options = {}) {
	const { mutation = {}, client: config = {} } = options ?? {};
	const { client: queryClient, ...mutationOptions } = mutation;
	const mutationKey = mutationOptions.mutationKey ?? exportRequestsMutationKey();
	const baseOptions = exportRequestsMutationOptions(config);
	return useMutation({
		...baseOptions,
		mutationKey,
		...mutationOptions
	}, queryClient);
}
//#endregion
//#region src/__generated__/external/application/hooks/useGetApplicantNames.ts
const getApplicantNamesQueryKey = ({ query } = {}) => [{ url: "/api/mdm/external/api/external/requests/applicant-names" }, ...query ? [query] : []];
function getApplicantNamesQueryOptions({ query } = {}, config = {}) {
	const queryKey = getApplicantNamesQueryKey({ query });
	return queryOptions({
		queryKey,
		queryFn: async ({ signal }) => {
			const { data } = await getApplicantNames({
				...config,
				query,
				signal: config.signal ?? signal,
				throwOnError: true
			});
			return data;
		}
	});
}
/**
* @description Наименования адресатов из собственных заявок текущего пользователя —
* для выпадающего списка фильтра. value и displayName совпадают:
* фильтруем по самому наименованию, id тут нет.
* Поиск q — подстрока, без учёта регистра; q и limit применяются в
* запросе, поэтому выборка ничего не теряет.
* selected — уже выбранные значения. Возвращаются всегда, вне q и limit.
* Значения остальных фильтров Заявителя — закрытые наборы (статус,
* тип заявки, железная дорога) и приходят из
* GET /api/external/dictionaries.
* @summary Доступные значения фильтра «Заявитель»
* {@link /api/mdm/external/api/external/requests/applicant-names}
*/
function useGetApplicantNames({ query } = {}, options = {}) {
	const { query: queryConfig = {}, client: config = {} } = options ?? {};
	const { client: queryClient, ...resolvedOptions } = queryConfig;
	const resolvedParams = { query: typeof query === "function" ? query() : query };
	const queryKey = resolvedOptions?.queryKey ?? getApplicantNamesQueryKey(resolvedParams);
	const queryResult = useQuery({
		...getApplicantNamesQueryOptions(resolvedParams, config),
		...resolvedOptions,
		queryKey
	}, queryClient);
	queryResult.queryKey = queryKey;
	return queryResult;
}
//#endregion
//#region src/__generated__/external/application/hooks/useGetCounts.ts
const getCountsQueryKey = () => [{ url: "/api/mdm/external/api/external/requests/counts" }];
function getCountsQueryOptions(config = {}) {
	const queryKey = getCountsQueryKey();
	return queryOptions({
		queryKey,
		queryFn: async ({ signal }) => {
			const { data } = await getCounts({
				...config,
				signal: config.signal ?? signal,
				throwOnError: true
			});
			return data;
		}
	});
}
/**
* @description Для текущего пользователя: total, разбивка по статусам и needsAttention (WAITING_PAYMENT + REVISION_REQUIRED).
* @summary Счётчики заявок по статусам
* {@link /api/mdm/external/api/external/requests/counts}
*/
function useGetCounts(options = {}) {
	const { query: queryConfig = {}, client: config = {} } = options ?? {};
	const { client: queryClient, ...resolvedOptions } = queryConfig;
	const queryKey = resolvedOptions?.queryKey ?? getCountsQueryKey();
	const queryResult = useQuery({
		...getCountsQueryOptions(config),
		...resolvedOptions,
		queryKey
	}, queryClient);
	queryResult.queryKey = queryKey;
	return queryResult;
}
//#endregion
//#region src/__generated__/external/application/hooks/useGetHistory.ts
const getHistoryQueryKey = ({ path }) => [{
	url: "/api/mdm/external/api/external/requests/:id/history",
	params: path
}];
function getHistoryQueryOptions({ path }, config = {}) {
	const queryKey = getHistoryQueryKey({ path });
	return queryOptions({
		queryKey,
		queryFn: async ({ signal }) => {
			const { data } = await getHistory({
				...config,
				path,
				signal: config.signal ?? signal,
				throwOnError: true
			});
			return data;
		}
	});
}
/**
* @description Журнал событий (eventType, actor, actorRole, payload, occurredAt),
* отсортированный по убыванию времени. Доступ как у detail: владелец
* и OPERATOR (для не-DRAFT). Иначе — REQUEST_NOT_FOUND.
* @summary История действий по заявке
* {@link /api/mdm/external/api/external/requests/:id/history}
*/
function useGetHistory({ path }, options = {}) {
	const { query: queryConfig = {}, client: config = {} } = options ?? {};
	const { client: queryClient, ...resolvedOptions } = queryConfig;
	const resolvedParams = { path: typeof path === "function" ? path() : path };
	const queryKey = resolvedOptions?.queryKey ?? getHistoryQueryKey(resolvedParams);
	const queryResult = useQuery({
		...getHistoryQueryOptions(resolvedParams, config),
		...resolvedOptions,
		queryKey
	}, queryClient);
	queryResult.queryKey = queryKey;
	return queryResult;
}
//#endregion
//#region src/__generated__/external/application/hooks/useGetNeedsAttention.ts
const getNeedsAttentionQueryKey = ({ query } = {}) => [{ url: "/api/mdm/external/api/external/requests/needs-attention" }, ...query ? [query] : []];
function getNeedsAttentionQueryOptions({ query } = {}, config = {}) {
	const queryKey = getNeedsAttentionQueryKey({ query });
	return queryOptions({
		queryKey,
		queryFn: async ({ signal }) => {
			const { data } = await getNeedsAttention({
				...config,
				query,
				signal: config.signal ?? signal,
				throwOnError: true
			});
			return data;
		}
	});
}
/**
* @description Заявитель — свои заявки в статусах «Требуется доработка» и «Ожидает оплаты»;
* Оператор — заявки, требующие выгрузки. Роль без блока → пустой список.
* totalElements = число в заголовке блока. Поиск search — ILIKE по title;
* дополнительно — префиксное совпадение по number (как в GET /requests).
* @summary Блок «Требует внимания»
* {@link /api/mdm/external/api/external/requests/needs-attention}
*/
function useGetNeedsAttention({ query } = {}, options = {}) {
	const { query: queryConfig = {}, client: config = {} } = options ?? {};
	const { client: queryClient, ...resolvedOptions } = queryConfig;
	const resolvedParams = { query: typeof query === "function" ? query() : query };
	const queryKey = resolvedOptions?.queryKey ?? getNeedsAttentionQueryKey(resolvedParams);
	const queryResult = useQuery({
		...getNeedsAttentionQueryOptions(resolvedParams, config),
		...resolvedOptions,
		queryKey
	}, queryClient);
	queryResult.queryKey = queryKey;
	return queryResult;
}
//#endregion
//#region src/__generated__/external/application/hooks/useGetOperatorCounts.ts
const getOperatorCountsQueryKey = () => [{ url: "/api/mdm/external/api/external/operator/requests/counts" }];
function getOperatorCountsQueryOptions(config = {}) {
	const queryKey = getOperatorCountsQueryKey();
	return queryOptions({
		queryKey,
		queryFn: async ({ signal }) => {
			const { data } = await getOperatorCounts({
				...config,
				signal: config.signal ?? signal,
				throwOnError: true
			});
			return data;
		}
	});
}
/**
* @description Агрегат по всем заявкам (кроме DRAFT): total, разбивка byStatus и
* needsAttention (WAITING_PAYMENT + REVISION_REQUIRED). Требует роль OPERATOR.
* @summary Счётчики заявок для дашборда Оператора
* {@link /api/mdm/external/api/external/operator/requests/counts}
*/
function useGetOperatorCounts(options = {}) {
	const { query: queryConfig = {}, client: config = {} } = options ?? {};
	const { client: queryClient, ...resolvedOptions } = queryConfig;
	const queryKey = resolvedOptions?.queryKey ?? getOperatorCountsQueryKey();
	const queryResult = useQuery({
		...getOperatorCountsQueryOptions(config),
		...resolvedOptions,
		queryKey
	}, queryClient);
	queryResult.queryKey = queryKey;
	return queryResult;
}
//#endregion
//#region src/__generated__/external/application/hooks/useGetOperatorFilterValues.ts
const getOperatorFilterValuesQueryKey = ({ query }) => [{ url: "/api/mdm/external/api/external/operator/requests/filter-values" }, ...query ? [query] : []];
function getOperatorFilterValuesQueryOptions({ query }, config = {}) {
	const queryKey = getOperatorFilterValuesQueryKey({ query });
	return queryOptions({
		queryKey,
		queryFn: async ({ signal }) => {
			const { data } = await getOperatorFilterValues({
				...config,
				query,
				signal: config.signal ?? signal,
				throwOnError: true
			});
			return data;
		}
	});
}
/**
* @description Значения для выпадающих списков фильтров «Оператор» и «Инициатор».
* Возвращаются только те пользователи, которые реально встречаются в
* заявках: остальные дали бы пустой результат. value — id пользователя,
* его и надо присылать в operatorId/initiatorId.
* Требует роль OPERATOR. Значения фильтров с закрытым набором (признак
* обработки, железная дорога) приходят из GET /api/external/dictionaries.
* Поиск q — подстрока без учёта регистра по фамилии, имени, логину или
* email; отчество не участвует. Ищется по всей директории Keycloak, а не
* только по началу списка, поэтому пользователь находится независимо от
* размера выборки. Без q список отдаёт первые значения по алфавиту.
* selected — уже выбранные id. Возвращаются всегда, вне q и limit.
* @summary Доступные значения фильтра по колонке
* {@link /api/mdm/external/api/external/operator/requests/filter-values}
*/
function useGetOperatorFilterValues({ query }, options = {}) {
	const { query: queryConfig = {}, client: config = {} } = options ?? {};
	const { client: queryClient, ...resolvedOptions } = queryConfig;
	const resolvedParams = { query: typeof query === "function" ? query() : query };
	const queryKey = resolvedOptions?.queryKey ?? getOperatorFilterValuesQueryKey(resolvedParams);
	const queryResult = useQuery({
		...getOperatorFilterValuesQueryOptions(resolvedParams, config),
		...resolvedOptions,
		queryKey
	}, queryClient);
	queryResult.queryKey = queryKey;
	return queryResult;
}
//#endregion
//#region src/__generated__/external/application/hooks/useGetOperatorRequests.ts
const getOperatorRequestsQueryKey = ({ query } = {}) => [{ url: "/api/mdm/external/api/external/operator/requests" }, ...query ? [query] : []];
function getOperatorRequestsQueryOptions({ query } = {}, config = {}) {
	const queryKey = getOperatorRequestsQueryKey({ query });
	return queryOptions({
		queryKey,
		queryFn: async ({ signal }) => {
			const { data } = await getOperatorRequests({
				...config,
				query,
				signal: config.signal ?? signal,
				throwOnError: true
			});
			return data;
		}
	});
}
/**
* @description         Возвращает список всех заявок (кроме DRAFT), доступных оператору,
*         в наборе колонок, специфичном для роли Оператор (признак обработки,
*         даты выгрузки/загрузки, ФИО оператора-актора).
*         Требует роль OPERATOR.
*         Заявки в статусе DRAFT никогда не возвращаются, даже если передан ?status=DRAFT.
*         Фильтры processingFlag/railway/operatorId/initiatorId — повторяемые параметры;
*         даты поступления/обработки — периоды *From/*To, оба конца включительно,
*         по тем же вычисляемым выражениям, что показываются в ячейках.
*         Сортировка sortBy/sortDir (дефолт — created_at DESC), вторичный ключ id.
*         sortDir принимает строго ASC или DESC — значение регистрозависимо,
*         ?sortDir=asc вернёт 400 (пустое значение допустимо и равно отсутствию).
*         sortDir без sortBy игнорируется: порядок остаётся created_at DESC.
*         sortBy без sortDir — ASC.
*         RECEIVED_AT/PROCESSED_AT/PROCESSING_FLAG — вычисляемые поля (совпадают
*         с логикой отображаемых значений). STATUS сортируется по отображаемому
*         названию статуса. OPERATOR/INITIATOR сортируются по snapshot ФИО
*         из event log; фильтрация по ним работает по id пользователя.
* @summary Список заявок для Оператора
* {@link /api/mdm/external/api/external/operator/requests}
*/
function useGetOperatorRequests({ query } = {}, options = {}) {
	const { query: queryConfig = {}, client: config = {} } = options ?? {};
	const { client: queryClient, ...resolvedOptions } = queryConfig;
	const resolvedParams = { query: typeof query === "function" ? query() : query };
	const queryKey = resolvedOptions?.queryKey ?? getOperatorRequestsQueryKey(resolvedParams);
	const queryResult = useQuery({
		...getOperatorRequestsQueryOptions(resolvedParams, config),
		...resolvedOptions,
		queryKey
	}, queryClient);
	queryResult.queryKey = queryKey;
	return queryResult;
}
//#endregion
//#region src/__generated__/external/application/hooks/useGetRequest.ts
const getRequestQueryKey = ({ path }) => [{
	url: "/api/mdm/external/api/external/requests/:id",
	params: path
}];
function getRequestQueryOptions({ path }, config = {}) {
	const queryKey = getRequestQueryKey({ path });
	return queryOptions({
		queryKey,
		queryFn: async ({ signal }) => {
			const { data } = await getRequest({
				...config,
				path,
				signal: config.signal ?? signal,
				throwOnError: true
			});
			return data;
		}
	});
}
/**
* @description         Возвращает детальную информацию о заявке по идентификатору.
*         Доступно владельцу заявки (в любом не-удалённом статусе) и роли OPERATOR
*         (для чужих заявок в любом статусе кроме DRAFT). Для скрытия существования
*         чужих и удалённых заявок во всех остальных случаях возвращает REQUEST_NOT_FOUND.
* @summary Детали заявки
* {@link /api/mdm/external/api/external/requests/:id}
*/
function useGetRequest({ path }, options = {}) {
	const { query: queryConfig = {}, client: config = {} } = options ?? {};
	const { client: queryClient, ...resolvedOptions } = queryConfig;
	const resolvedParams = { path: typeof path === "function" ? path() : path };
	const queryKey = resolvedOptions?.queryKey ?? getRequestQueryKey(resolvedParams);
	const queryResult = useQuery({
		...getRequestQueryOptions(resolvedParams, config),
		...resolvedOptions,
		queryKey
	}, queryClient);
	queryResult.queryKey = queryKey;
	return queryResult;
}
//#endregion
//#region src/__generated__/external/application/hooks/useGetRequests.ts
const getRequestsQueryKey = ({ query } = {}) => [{ url: "/api/mdm/external/api/external/requests" }, ...query ? [query] : []];
function getRequestsQueryOptions({ query } = {}, config = {}) {
	const queryKey = getRequestsQueryKey({ query });
	return queryOptions({
		queryKey,
		queryFn: async ({ signal }) => {
			const { data } = await getRequests({
				...config,
				query,
				signal: config.signal ?? signal,
				throwOnError: true
			});
			return data;
		}
	});
}
/**
* @description Возвращает пагинированный список заявок текущего пользователя.
* Поиск ILIKE по title и additional_data; дополнительно — префиксное
* совпадение по number (из строки берутся все цифры).
* Фильтры status/paymentCondition/requestSubject/railway/applicantNames — повторяемые
* параметры (множественный выбор), даты — периоды *From/*To, оба конца
* включительно. applicantNames — точные значения из
* GET /requests/applicant-names, не подстрока.
* paymentCondition берётся из payment_flag, установленного Аппаратом НГ и
* импортированного из внутреннего контура; при отсутствии признака — UNDER_REVIEW
* (прочерк в UI).
* Сортировка sortBy/sortDir (дефолт — created_at DESC), вторичный ключ id.
* sortDir принимает строго ASC или DESC — значение регистрозависимо,
* ?sortDir=asc вернёт 400 (пустое значение допустимо и равно отсутствию).
* sortDir без sortBy игнорируется: порядок остаётся created_at DESC.
* sortBy без sortDir — ASC.
* STATUS/RAILWAY/REQUEST_SUBJECT сортируются по отображаемому названию,
* PAYMENT_CONDITION — по «На рассмотрении»/«Без оплаты»/«Платная услуга»;
* без учёта регистра для текстовых полей.
* @summary Мои заявки
* {@link /api/mdm/external/api/external/requests}
*/
function useGetRequests({ query } = {}, options = {}) {
	const { query: queryConfig = {}, client: config = {} } = options ?? {};
	const { client: queryClient, ...resolvedOptions } = queryConfig;
	const resolvedParams = { query: typeof query === "function" ? query() : query };
	const queryKey = resolvedOptions?.queryKey ?? getRequestsQueryKey(resolvedParams);
	const queryResult = useQuery({
		...getRequestsQueryOptions(resolvedParams, config),
		...resolvedOptions,
		queryKey
	}, queryClient);
	queryResult.queryKey = queryKey;
	return queryResult;
}
//#endregion
//#region src/__generated__/external/application/hooks/useGetRequestsInfinite.ts
const getRequestsInfiniteQueryKey = ({ query } = {}) => [{ url: "/api/mdm/external/api/external/requests" }, ...query ? [query] : []];
function getRequestsInfiniteQueryOptions({ query } = {}, config = {}) {
	const queryKey = getRequestsInfiniteQueryKey({ query });
	return infiniteQueryOptions({
		queryKey,
		queryFn: async ({ signal, pageParam }) => {
			query = {
				...query ?? {},
				["page"]: pageParam
			};
			const { data } = await getRequests({
				...config,
				query,
				signal: config.signal ?? signal,
				throwOnError: true
			});
			return data;
		},
		initialPageParam: 0,
		getNextPageParam: (lastPage, _allPages, lastPageParam) => Array.isArray(lastPage) && lastPage.length === 0 ? void 0 : lastPageParam + 1,
		getPreviousPageParam: (_firstPage, _allPages, firstPageParam) => firstPageParam <= 1 ? void 0 : firstPageParam - 1
	});
}
/**
* @description Возвращает пагинированный список заявок текущего пользователя.
* Поиск ILIKE по title и additional_data; дополнительно — префиксное
* совпадение по number (из строки берутся все цифры).
* Фильтры status/paymentCondition/requestSubject/railway/applicantNames — повторяемые
* параметры (множественный выбор), даты — периоды *From/*To, оба конца
* включительно. applicantNames — точные значения из
* GET /requests/applicant-names, не подстрока.
* paymentCondition берётся из payment_flag, установленного Аппаратом НГ и
* импортированного из внутреннего контура; при отсутствии признака — UNDER_REVIEW
* (прочерк в UI).
* Сортировка sortBy/sortDir (дефолт — created_at DESC), вторичный ключ id.
* sortDir принимает строго ASC или DESC — значение регистрозависимо,
* ?sortDir=asc вернёт 400 (пустое значение допустимо и равно отсутствию).
* sortDir без sortBy игнорируется: порядок остаётся created_at DESC.
* sortBy без sortDir — ASC.
* STATUS/RAILWAY/REQUEST_SUBJECT сортируются по отображаемому названию,
* PAYMENT_CONDITION — по «На рассмотрении»/«Без оплаты»/«Платная услуга»;
* без учёта регистра для текстовых полей.
* @summary Мои заявки
* {@link /api/mdm/external/api/external/requests}
*/
function useGetRequestsInfinite({ query } = {}, options = {}) {
	const { query: queryConfig = {}, client: config = {} } = options ?? {};
	const { client: queryClient, ...resolvedOptions } = queryConfig;
	const resolvedParams = { query: typeof query === "function" ? query() : query };
	const queryKey = resolvedOptions?.queryKey ?? getRequestsInfiniteQueryKey(resolvedParams);
	const queryResult = useInfiniteQuery({
		...getRequestsInfiniteQueryOptions(resolvedParams, config),
		...resolvedOptions,
		queryKey
	}, queryClient);
	queryResult.queryKey = queryKey;
	return queryResult;
}
//#endregion
//#region src/__generated__/external/application/hooks/useImportResult.ts
const importResultMutationKey = () => [{ url: "/api/mdm/external/api/external/operator/results/import" }];
function importResultMutationOptions(config = {}) {
	const mutationKey = importResultMutationKey();
	return mutationOptions({
		mutationKey,
		mutationFn: async ({ body }) => {
			const { data } = await importResult({
				...config,
				body,
				throwOnError: true
			});
			return data;
		}
	});
}
/**
* @description Импортирует ZIP-архив с итоговым результатом по заявке
* @summary Импорт архива итогового результата
* {@link /api/mdm/external/api/external/operator/results/import}
*/
function useImportResult(options = {}) {
	const { mutation = {}, client: config = {} } = options ?? {};
	const { client: queryClient, ...mutationOptions } = mutation;
	const mutationKey = mutationOptions.mutationKey ?? importResultMutationKey();
	const baseOptions = importResultMutationOptions(config);
	return useMutation({
		...baseOptions,
		mutationKey,
		...mutationOptions
	}, queryClient);
}
//#endregion
//#region src/__generated__/external/application/hooks/useListAttachmentDescriptions.ts
const listAttachmentDescriptionsQueryKey = ({ path }) => [{
	url: "/api/mdm/external/api/external/requests/:id/attachments",
	params: path
}];
function listAttachmentDescriptionsQueryOptions({ path }, config = {}) {
	const queryKey = listAttachmentDescriptionsQueryKey({ path });
	return queryOptions({
		queryKey,
		queryFn: async ({ signal }) => {
			const { data } = await listAttachmentDescriptions({
				...config,
				path,
				signal: config.signal ?? signal,
				throwOnError: true
			});
			return data;
		}
	});
}
/**
* @description Список {fileId, description}. Доступ как у detail (владелец; OPERATOR — для не-DRAFT).
* @summary Описания вложений заявки
* {@link /api/mdm/external/api/external/requests/:id/attachments}
*/
function useListAttachmentDescriptions({ path }, options = {}) {
	const { query: queryConfig = {}, client: config = {} } = options ?? {};
	const { client: queryClient, ...resolvedOptions } = queryConfig;
	const resolvedParams = { path: typeof path === "function" ? path() : path };
	const queryKey = resolvedOptions?.queryKey ?? listAttachmentDescriptionsQueryKey(resolvedParams);
	const queryResult = useQuery({
		...listAttachmentDescriptionsQueryOptions(resolvedParams, config),
		...resolvedOptions,
		queryKey
	}, queryClient);
	queryResult.queryKey = queryKey;
	return queryResult;
}
//#endregion
//#region src/__generated__/external/application/hooks/useRejectRequest.ts
const rejectRequestMutationKey = () => [{ url: "/api/mdm/external/api/external/operator/requests/:id/reject" }];
function rejectRequestMutationOptions(config = {}) {
	const mutationKey = rejectRequestMutationKey();
	return mutationOptions({
		mutationKey,
		mutationFn: async ({ path }) => {
			const { data } = await rejectRequest({
				...config,
				path,
				throwOnError: true
			});
			return data;
		}
	});
}
/**
* @description         Переводит заявку в статус RESOLVED_REJECTED. Доступно только для OPERATOR.
*         Допустимые исходные статусы: IN_REVIEW.
*         Возвращает обновлённое состояние заявки.
* @summary Отклонить заявку
* {@link /api/mdm/external/api/external/operator/requests/:id/reject}
*/
function useRejectRequest(options = {}) {
	const { mutation = {}, client: config = {} } = options ?? {};
	const { client: queryClient, ...mutationOptions } = mutation;
	const mutationKey = mutationOptions.mutationKey ?? rejectRequestMutationKey();
	const baseOptions = rejectRequestMutationOptions(config);
	return useMutation({
		...baseOptions,
		mutationKey,
		...mutationOptions
	}, queryClient);
}
//#endregion
//#region src/__generated__/external/application/hooks/useSetAttachmentDescription.ts
const setAttachmentDescriptionMutationKey = () => [{ url: "/api/mdm/external/api/external/requests/:id/attachments/:fileId" }];
function setAttachmentDescriptionMutationOptions(config = {}) {
	const mutationKey = setAttachmentDescriptionMutationKey();
	return mutationOptions({
		mutationKey,
		mutationFn: async ({ path, body }) => {
			const { data } = await setAttachmentDescription({
				...config,
				path,
				body,
				throwOnError: true
			});
			return data;
		}
	});
}
/**
* @description Upsert описания файла. Только владелец, только в статусе DRAFT.
* @summary Установить описание вложения
* {@link /api/mdm/external/api/external/requests/:id/attachments/:fileId}
*/
function useSetAttachmentDescription(options = {}) {
	const { mutation = {}, client: config = {} } = options ?? {};
	const { client: queryClient, ...mutationOptions } = mutation;
	const mutationKey = mutationOptions.mutationKey ?? setAttachmentDescriptionMutationKey();
	const baseOptions = setAttachmentDescriptionMutationOptions(config);
	return useMutation({
		...baseOptions,
		mutationKey,
		...mutationOptions
	}, queryClient);
}
//#endregion
//#region src/__generated__/external/application/hooks/useSubmitRequest.ts
const submitRequestMutationKey = () => [{ url: "/api/mdm/external/api/external/requests/:id/submit" }];
function submitRequestMutationOptions(config = {}) {
	const mutationKey = submitRequestMutationKey();
	return mutationOptions({
		mutationKey,
		mutationFn: async ({ path }) => {
			const { data } = await submitRequest({
				...config,
				path,
				throwOnError: true
			});
			return data;
		}
	});
}
/**
* @description Переводит заявку из статуса DRAFT или REVISION_REQUIRED (повторная подача) в статус IN_REVIEW
* @summary Отправить заявку (DRAFT/REVISION_REQUIRED → IN_REVIEW)
* {@link /api/mdm/external/api/external/requests/:id/submit}
*/
function useSubmitRequest(options = {}) {
	const { mutation = {}, client: config = {} } = options ?? {};
	const { client: queryClient, ...mutationOptions } = mutation;
	const mutationKey = mutationOptions.mutationKey ?? submitRequestMutationKey();
	const baseOptions = submitRequestMutationOptions(config);
	return useMutation({
		...baseOptions,
		mutationKey,
		...mutationOptions
	}, queryClient);
}
//#endregion
//#region src/__generated__/external/application/hooks/useUpdateDraft.ts
const updateDraftMutationKey = () => [{ url: "/api/mdm/external/api/external/requests/:id" }];
function updateDraftMutationOptions(config = {}) {
	const mutationKey = updateDraftMutationKey();
	return mutationOptions({
		mutationKey,
		mutationFn: async ({ path, body }) => {
			const { data } = await updateDraft({
				...config,
				path,
				body,
				throwOnError: true
			});
			return data;
		}
	});
}
/**
* @description Меняет поля заявки в статусе DRAFT. Поля с null остаются прежними;
* пустая строка для additionalData очищает поле. Чужие заявки маскируются
* под REQUEST_NOT_FOUND, как и в detail/submit.
* @summary Редактировать черновик заявки
* {@link /api/mdm/external/api/external/requests/:id}
*/
function useUpdateDraft(options = {}) {
	const { mutation = {}, client: config = {} } = options ?? {};
	const { client: queryClient, ...mutationOptions } = mutation;
	const mutationKey = mutationOptions.mutationKey ?? updateDraftMutationKey();
	const baseOptions = updateDraftMutationOptions(config);
	return useMutation({
		...baseOptions,
		mutationKey,
		...mutationOptions
	}, queryClient);
}
//#endregion
export { deleteRequestMutationOptions as $, useGetOperatorRequests as A, getHistoryQueryKey as B, getRequestsQueryOptions as C, useGetRequest as D, getRequestQueryOptions as E, getOperatorCountsQueryOptions as F, useGetCounts as G, useGetHistory as H, useGetOperatorCounts as I, useGetApplicantNames as J, getApplicantNamesQueryKey as K, getNeedsAttentionQueryKey as L, getOperatorFilterValuesQueryOptions as M, useGetOperatorFilterValues as N, getOperatorRequestsQueryKey as O, getOperatorCountsQueryKey as P, deleteRequestMutationKey as Q, getNeedsAttentionQueryOptions as R, getRequestsQueryKey as S, getRequestQueryKey as T, getCountsQueryKey as U, getHistoryQueryOptions as V, getCountsQueryOptions as W, exportRequestsMutationOptions as X, exportRequestsMutationKey as Y, useExportRequests as Z, importResultMutationOptions as _, submitRequestMutationOptions as a, createRequestMutationOptions as at, getRequestsInfiniteQueryOptions as b, setAttachmentDescriptionMutationOptions as c, rejectRequestMutationOptions as d, useDeleteRequest as et, useRejectRequest as f, importResultMutationKey as g, useListAttachmentDescriptions as h, submitRequestMutationKey as i, createRequestMutationKey as it, getOperatorFilterValuesQueryKey as j, getOperatorRequestsQueryOptions as k, useSetAttachmentDescription as l, listAttachmentDescriptionsQueryOptions as m, updateDraftMutationOptions as n, deleteAttachmentDescriptionMutationOptions as nt, useSubmitRequest as o, useCreateRequest as ot, listAttachmentDescriptionsQueryKey as p, getApplicantNamesQueryOptions as q, useUpdateDraft as r, useDeleteAttachmentDescription as rt, setAttachmentDescriptionMutationKey as s, updateDraftMutationKey as t, deleteAttachmentDescriptionMutationKey as tt, rejectRequestMutationKey as u, useImportResult as v, useGetRequests as w, useGetRequestsInfinite as x, getRequestsInfiniteQueryKey as y, useGetNeedsAttention as z };

//# sourceMappingURL=useUpdateDraft.js.map